import { useQuery } from '@apollo/client'
import { useAccessStore } from 'features/Access/infra/react/AccessState'
import { SearchIcon, TriangleExclamationIcon } from 'presentation/components/Icons'
import { NoticeButton, NoticeEmphasis } from 'presentation/components/Notice'
import { ResponsiveNotice } from 'presentation/components/ResponsiveNotice/ResponsiveNotice'
import {
  SUBSCRIPTION_NOTICE__GET_SUBSCRIPTION,
  SUBSCRIPTION_NOTICE_QUERY_OPTIONS,
} from 'presentation/main/GlobalNotice/SubscriptionNotice/SubscriptionNotice'
import { useNoticeStore } from 'presentation/main/NoticeCarousel'
import { NOTICE_KEYS } from 'presentation/main/NoticeCarousel/Notice.const'
import { CHOOSE_PLAN_PATH, hasLegacyPastDueInvoice } from 'presentation/main/globalModals/FailedInvoiceModal/legacyPastDuePlan'
import { handleDefaultOpenFailedInvoiceModal } from 'presentation/main/globalModals/FailedInvoiceModal/handleDefaultOpenFailedInvoiceModal'
import { unmaskFailedInvoiceFragment } from 'presentation/main/globalModals/FailedInvoiceModal/unmaskFailedInvoiceFragment'
import { get } from 'presentation/utils/graphql'
import { useCallback, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'

export const usePaymentFailedNotice = () => {
  const access = useAccessStore(state => state.local.access)
  const { data, loading, error } = useQuery<any>(
    SUBSCRIPTION_NOTICE__GET_SUBSCRIPTION,
    SUBSCRIPTION_NOTICE_QUERY_OPTIONS,
  )

  const sub = data?.myEnterprise?.subscription
  const activeSub = get('BillingSubscriptionActive')(sub) ?? undefined

  const failedInvoiceSummary = useMemo(() =>
    unmaskFailedInvoiceFragment(activeSub), [activeSub])
  const isLegacyPlan = activeSub?.plan?.product?.isLegacy ?? false
  const hasPastDueInvoice = !!failedInvoiceSummary
  const hasCurrentAccessSubscription
    = (access.status === 'loaded' || access.status === 'updating')
    && access.data.status === 'logged-in'
    && !!access.data.subscription
  const hasLegacyInvoice = hasLegacyPastDueInvoice({
    failedInvoiceSummary,
    isLegacyPlan,
  })

  const handleTryPaymentAgain = useCallback(() => {
    handleDefaultOpenFailedInvoiceModal({
      failedInvoiceSummary,
      isLegacyPlan,
    })
  }, [failedInvoiceSummary, isLegacyPlan])

  const updateNotice = useNoticeStore(store => store.updateNotice)

  useEffect(() => {
    if (loading || error) return

    if (hasCurrentAccessSubscription && hasPastDueInvoice) {
      updateNotice(NOTICE_KEYS.PAYMENT_FAILED, (
        <ResponsiveNotice
          colorScheme='error'
          icon={TriangleExclamationIcon}
          textContent={(
            <>
              {hasLegacyInvoice
                ? (
                  <>
                    <NoticeEmphasis>
                      Your previous plan is no longer available.
                    </NoticeEmphasis>
                    {' '}
                    Choose a current plan to continue using Propelio.
                  </>
                )
                : (
                  <>
                    <NoticeEmphasis>
                      Uh-Oh! We were unable to process your payment.
                    </NoticeEmphasis>
                    {' '}
                    Your account is
                    {' '}
                    <NoticeEmphasis>past due</NoticeEmphasis>
                    {' '}
                    and
                    scheduled for deletion this month.
                  </>
                )}
            </>
          )}
          actionsContent={(
            <>
              {hasLegacyInvoice
                ? (
                  <NoticeButton
                    variant='solid'
                    colorScheme='highlight'
                    as={Link}
                    to={CHOOSE_PLAN_PATH}
                  >
                    Choose Plan
                  </NoticeButton>
                )
                : (
                  <NoticeButton
                    variant='solid'
                    colorScheme='highlight'
                    onClick={handleTryPaymentAgain}
                    leftIcon={<SearchIcon boxSize='2' />}
                  >
                    review payment
                  </NoticeButton>
                )}
            </>
          )}
        />
      ))
    }

    return () => updateNotice(NOTICE_KEYS.PAYMENT_FAILED, null)
  }, [
    loading,
    error,
    hasCurrentAccessSubscription,
    hasPastDueInvoice,
    hasLegacyInvoice,
    handleTryPaymentAgain,
  ])
}
